/*
 * MODE unlock appointment picker.
 *
 * Vanilla JS, no dependencies, no calendar library. One instance per section.
 *
 * The flow, and the reason for its order:
 *
 *   workshop -> date -> time -> HOLD -> cart line
 *
 * The hold is created the moment a time is picked, before anything reaches the cart.
 * That is the whole design: the slot is live inventory, so it has to be taken off the
 * board server-side before the customer starts checking out. Everything after that -
 * the countdown, the cart line, the hidden properties - is bookkeeping around a
 * reservation that already exists in Postgres.
 *
 * Things this file deliberately does NOT do:
 *   - decide whether a slot is free (only the server can)
 *   - trust its own idea of the time (the server derives duration and end time)
 *   - build a cart UI (it reuses whatever the theme already has)
 *   - call alert() (every message goes to the status line)
 */

(function () {
  'use strict';

  var HOLD_ID_PROP = '_mode_booking_hold_id';
  var TOKEN_PROP = '_mode_booking_token';
  var START_PROP = '_mode_booking_start_utc';
  var END_PROP = '_mode_booking_end_utc';
  var WORKSHOP_PROP = 'Workshop';
  var APPOINTMENT_PROP = 'Appointment';

  var WEEKDAY_OFFSET = 1; // calendar grid starts on Monday

  function ModeBooking(root) {
    this.root = root;
    this.proxyBase = root.dataset.proxyBase || '/apps/mode-booking';
    this.daysAhead = parseInt(root.dataset.daysAhead, 10) || 28;
    this.refreshMs = (parseInt(root.dataset.refreshSeconds, 10) || 10) * 1000;
    this.showAddress = root.dataset.showAddress === 'true';
    this.sectionId = root.dataset.sectionId || '';
    this.productId = root.dataset.productId || '';
    this.productFormId = root.dataset.productFormId || '';
    this.visitorId = visitorId();

    // Copy comes from the Liquid data attributes so it stays translatable, with
    // English fallbacks so a stale locale file degrades instead of blanking out.
    this.copy = {
      loadFailed: root.dataset.messageLoadFailed ||
        'We could not load appointment times just now. Please refresh the page.',
      slotTaken: root.dataset.messageSlotTaken ||
        'That time was just booked - we have refreshed the available times.',
      existingHold: root.dataset.messageExistingHold ||
        'You already have a time held in another tab. Finish that booking, or wait for it to expire.',
      holdLimit: root.dataset.messageHoldLimit ||
        'You can hold three appointments at a time. Add one to your cart, or wait for a hold to expire.',
      holdFailed: root.dataset.messageHoldFailed ||
        'We could not hold that time. Please try again.',
      cartStale: root.dataset.messageCartStale ||
        'Your cart still has the old time until you pick a new one.',
      hint: root.dataset.messageHint || 'Choose an appointment time above to continue.'
    };

    this.variantId = root.dataset.variantId || null;
    this.config = null;
    this.locations = [];
    this.location = null;
    this.availability = null;
    this.selectedDate = null;
    this.visibleMonth = null;
    this.hold = null;
    this.busy = false;
    this.destroyed = false;
    this.submitting = false;
    this.abortController = null;
    this.inflight = null;

    this.el = {
      card: root.querySelector('.mode-booking__card'),
      locationStep: root.querySelector('[data-step="location"]'),
      locationSelect: root.querySelector('[data-location-select]'),
      locationAddress: root.querySelector('[data-location-address]'),
      dateStep: root.querySelector('[data-step="date"]'),
      calendarMonth: root.querySelector('[data-calendar-month]'),
      calendarDays: root.querySelector('[data-calendar-days]'),
      calendarPrev: root.querySelector('[data-calendar-prev]'),
      calendarNext: root.querySelector('[data-calendar-next]'),
      timeStep: root.querySelector('[data-step="time"]'),
      slots: root.querySelector('[data-slots]'),
      held: root.querySelector('[data-held]'),
      heldWhen: root.querySelector('[data-held-when]'),
      heldWhere: root.querySelector('[data-held-where]'),
      countdown: root.querySelector('[data-countdown]'),
      changeTime: root.querySelector('[data-change-time]'),
      status: root.querySelector('[data-status]'),
      loading: root.querySelector('[data-loading]')
    };

    this.form = this.findProductForm();
    this.bind();
    this.init();
  }

  //--------------------------------------------------------------------------
  // Plumbing
  //--------------------------------------------------------------------------

  ModeBooking.prototype.variantGid = function () {
    return this.variantId ? 'gid://shopify/ProductVariant/' + this.variantId : null;
  };

  /**
   * The theme's own add-to-cart form. We never replace it; we only read the selected
   * variant from it and, once a hold exists, disable or enable its submit button.
   */
  ModeBooking.prototype.findProductForm = function () {
    if (this.productFormId) {
      var exact = document.getElementById(this.productFormId);
      if (exact && exact.matches('form[action*="/cart/add"]')) return exact;
    }
    if (/^\d+$/.test(this.productId)) {
      var productForms = document.querySelectorAll(
        'form[action*="/cart/add"][data-product-form][data-product-id="' + this.productId + '"]'
      );
      if (productForms.length === 1) return productForms[0];
    }
    var scope = this.root.closest('[id^="shopify-section-"]');
    if (!scope) return null;
    var forms = scope.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      if (forms[i].querySelector('[name="id"]')) return forms[i];
    }
    return null;
  };

  ModeBooking.prototype.currentFormVariantId = function () {
    if (!this.form) return null;
    var input = this.form.querySelector('[name="id"]');
    return input && input.value ? String(input.value) : null;
  };

  ModeBooking.prototype.api = function (path, options) {
    var opts = options || {};
    var started = performance.now();
    return fetch(this.proxyBase + path, {
      method: opts.method || 'GET',
      headers: opts.body
        ? { 'Content-Type': 'application/json', Accept: 'application/json' }
        : { Accept: 'application/json' },
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      credentials: 'same-origin',
      signal: opts.signal
    }).then(function (response) {
      if (window.console && console.debug) {
        console.debug('[mode-booking timing]', {
          path: path.split('?')[0],
          shopify_proxy_ms: Math.round((performance.now() - started) * 10) / 10,
          server_timing: response.headers.get('server-timing') || null
        });
      }
      return response
        .json()
        .catch(function () {
          return {};
        })
        .then(function (body) {
          if (!response.ok) {
            var err = new Error((body.error && body.error.message) || 'Request failed');
            err.code = (body.error && body.error.code) || 'INTERNAL_ERROR';
            err.status = response.status;
            throw err;
          }
          return body;
        });
    });
  };

  //--------------------------------------------------------------------------
  // Rendering helpers
  //--------------------------------------------------------------------------

  ModeBooking.prototype.show = function (el, visible) {
    if (el) el.hidden = !visible;
  };

  /**
   * Show a message, and remember who put it there.
   *
   * The owner matters. Availability is polled every few seconds, and every poll
   * redraws the calendar - which used to clear the status line unconditionally. That
   * gave every error the customer needed to act on a lifespan of one poll interval:
   * "You already have a time held" appeared, then silently vanished before it could
   * be read. A redraw may now only clear a message the redraw itself put there.
   *
   * @param {string|null} message - null clears, subject to the owner rule.
   * @param {string} [tone] - info (default), warn or error.
   * @param {string} [owner] - 'action' (default, sticky) or 'calendar' (transient).
   */
  ModeBooking.prototype.setStatus = function (message, tone, owner) {
    var el = this.el.status;
    if (!el) return;
    if (!message) {
      this.statusOwner = null;
      el.hidden = true;
      el.textContent = '';
      return;
    }
    this.statusOwner = owner || 'action';
    el.hidden = false;
    el.textContent = message;
    el.className = 'mode-booking__status mode-booking__status--' + (tone || 'info');
  };

  /** Clear only if `owner` is the one who set the message currently showing. */
  ModeBooking.prototype.clearStatusFor = function (owner) {
    if (this.statusOwner && this.statusOwner !== owner) return;
    this.setStatus(null);
  };

  ModeBooking.prototype.setBusy = function (busy) {
    this.busy = busy;
    this.show(this.el.loading, busy);
  };

  //--------------------------------------------------------------------------
  // Boot
  //--------------------------------------------------------------------------

  ModeBooking.prototype.init = function () {
    var self = this;
    var formVariant = this.currentFormVariantId();
    if (formVariant) this.variantId = formVariant;

    this.loadForVariant().catch(function (err) {
      // A failure here means the widget cannot know whether a booking is required.
      // Hiding it silently would let a booking-required product be bought with no
      // appointment, so say so instead.
      self.root.hidden = false;
      self.setStatus(self.copy.loadFailed, 'error');
      if (window.console) console.error('[mode-booking]', err);
    });
  };

  ModeBooking.prototype.loadForVariant = function () {
    var self = this;
    var gid = this.variantGid();
    if (!gid) {
      this.root.hidden = true;
      if (this.form) this.form.classList.remove('mode-booking-required');
      this.setSubmitEnabled(true);
      this.restoreQuantity();
      return Promise.resolve();
    }

    // Fail closed while the proxy decides whether this variant is mapped. This avoids
    // a short enabled-button window during initial load or a rapid variant switch.
    this.setSubmitEnabled(false);
    this.setBusy(true);

    return this.api(
      '/bootstrap?variant_gid=' + encodeURIComponent(gid) + '&days=' + this.daysAhead
    )
      .then(function (bootstrap) {
        self.config = bootstrap.config;

        if (!self.config.bookable) {
          if (self.form) self.form.classList.remove('mode-booking-required');
          // A "Mail-in" variant, or a product not yet mapped in Supabase. Render
          // nothing, and make sure the buy button is not left disabled from a previous
          // variant.
          self.root.hidden = true;
          self.setSubmitEnabled(true);
          self.restoreQuantity();
          return self.discardHold('variant no longer requires a booking');
        }

        if (self.form) self.form.classList.add('mode-booking-required');
        self.forceQuantityOne();
        self.root.hidden = false;
        self.setSubmitEnabled(false);
        return self.loadLocations(bootstrap.locations, bootstrap.availability).then(function () {
          return self.restoreFromSession();
        });
      })
      .then(function () {
        self.setBusy(false);
      })
      .catch(function (err) {
        self.setBusy(false);
        throw err;
      });
  };

  ModeBooking.prototype.loadLocations = function (locations, initialAvailability) {
    var self = this;
    var applyLocations = function () {
      self.locations = locations || [];
      var select = self.el.locationSelect;
      if (!select) return;

      while (select.options.length > 1) select.remove(1);

      self.locations.forEach(function (loc) {
        var option = document.createElement('option');
        option.value = loc.id;
        option.textContent = loc.name;
        select.appendChild(option);
      });

      // One workshop means there is nothing to choose. Pick it and move on.
      if (self.locations.length === 1) {
        select.value = self.locations[0].id;
        self.show(self.el.locationStep, false);
        self.renumberSteps(true);
        self.location = self.locations[0];
        if (initialAvailability) {
          self.availability = initialAvailability;
          self.renderCalendar();
          return Promise.resolve();
        }
        return self.selectLocation(self.locations[0].id);
      }
      self.renumberSteps(false);
      self.show(self.el.locationStep, true);
      return undefined;
    };
    return Promise.resolve(applyLocations());
  };

  ModeBooking.prototype.renumberSteps = function (singleLocation) {
    var dateLabel = this.root.querySelector('[data-step="date"] .mode-booking__label');
    var timeLabel = this.root.querySelector('[data-time-label]');
    if (dateLabel) dateLabel.textContent = singleLocation ? '1. Choose a date' : '2. Choose a date';
    if (timeLabel) timeLabel.textContent = singleLocation ? '2. Choose a time' : '3. Choose a time';
  };

  //--------------------------------------------------------------------------
  // Step 1: workshop
  //--------------------------------------------------------------------------

  ModeBooking.prototype.selectLocation = function (locationId) {
    var self = this;
    this.location = this.locations.filter(function (l) {
      return l.id === locationId;
    })[0] || null;

    if (this.el.locationAddress) {
      var addr = this.location && this.location.address;
      this.el.locationAddress.textContent = addr || '';
      this.show(this.el.locationAddress, !!(addr && this.showAddress));
    }

    this.selectedDate = null;
    this.availability = null;
    this.show(this.el.timeStep, false);

    if (!this.location) {
      this.show(this.el.dateStep, false);
      return Promise.resolve();
    }

    this.visibleMonth = null;
    return this.loadAvailability();
  };

  //--------------------------------------------------------------------------
  // Availability
  //--------------------------------------------------------------------------

  /** Today's date at the workshop, which is not necessarily today in the browser. */
  ModeBooking.prototype.todayAtWorkshop = function () {
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: this.location.timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(new Date());
    } catch (e) {
      return new Date().toISOString().slice(0, 10);
    }
  };

  ModeBooking.prototype.loadAvailability = function (opts) {
    var self = this;
    var quiet = opts && opts.quiet;
    if (!this.location) return Promise.resolve();

    var from = (opts && opts.from) || this.todayAtWorkshop();
    var to = (opts && opts.to) || addDays(from, this.daysAhead - 1);

    var url =
      '/availability?variant_gid=' +
      encodeURIComponent(this.variantGid()) +
      '&location_id=' +
      encodeURIComponent(this.location.id) +
      '&from=' +
      from +
      '&to=' +
      to;

    if (!quiet) this.setBusy(true);

    // Settling a caller: busy state and error copy belong to whoever asked, not to the
    // request, because a background poll and a visible reload can share one request.
    var settle = function (body) {
      if (!quiet) self.setBusy(false);
      return body;
    };
    var fail = function (err) {
      if (err.name === 'AbortError') return undefined;
      if (!quiet) {
        self.setBusy(false);
        self.setStatus(err.message, 'error');
      }
      throw err;
    };

    // A poll tick landing on top of an explicit load asks the proxy the same question
    // twice. Join the request already in flight instead.
    if (this.inflight && this.inflight.url === url) {
      return this.inflight.promise.then(settle, fail);
    }

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    var request = this.api(url, { signal: this.abortController.signal }).then(function (body) {
      self.availability = body;
      self.renderCalendar();
      // If the day the customer is looking at just lost the time they were about to
      // pick, the chips redraw underneath them - which is the point of polling.
      if (self.selectedDate) self.renderSlots();
      return body;
    });
    var tracked = { url: url, promise: request };
    this.inflight = tracked;

    var clear = function () {
      // Only if nothing newer has taken its place: an aborted request still rejects,
      // and by then the load that replaced it owns the slot.
      if (self.inflight === tracked) self.inflight = null;
    };

    return request.then(
      function (body) {
        clear();
        // Measure the next poll from this answer, so an explicit load pushes the tick
        // out rather than stacking another request straight on top of it.
        self.restartPolling();
        return settle(body);
      },
      function (err) {
        clear();
        return fail(err);
      }
    );
  };

  ModeBooking.prototype.daysWithSlots = function () {
    var map = {};
    ((this.availability && this.availability.days) || []).forEach(function (day) {
      if (day.slots && day.slots.length) map[day.date] = day.slots;
    });
    return map;
  };

  //--------------------------------------------------------------------------
  // Step 2: the calendar
  //--------------------------------------------------------------------------

  ModeBooking.prototype.renderCalendar = function () {
    var self = this;
    var grid = this.el.calendarDays;
    if (!grid) return;

    var open = this.daysWithSlots();
    var openDates = Object.keys(open).sort();

    this.show(this.el.dateStep, true);

    if (!openDates.length) {
      grid.textContent = '';
      this.el.calendarMonth.textContent = '';
      this.setStatus(
        'No times are available at this workshop in the next few weeks. Please try another city or contact us.',
        'warn',
        'calendar'
      );
      return;
    }
    this.clearStatusFor('calendar');

    if (!this.visibleMonth) this.visibleMonth = openDates[0].slice(0, 7);

    var year = parseInt(this.visibleMonth.slice(0, 4), 10);
    var month = parseInt(this.visibleMonth.slice(5, 7), 10);

    this.el.calendarMonth.textContent = monthLabel(year, month);

    // Only offer navigation to months that actually contain an open day.
    var months = uniq(
      openDates.map(function (d) {
        return d.slice(0, 7);
      })
    );
    var index = months.indexOf(this.visibleMonth);
    this.el.calendarPrev.disabled = index <= 0;
    this.el.calendarNext.disabled = index === -1 || index >= months.length - 1;

    var first = new Date(Date.UTC(year, month - 1, 1));
    var daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    var lead = (first.getUTCDay() - WEEKDAY_OFFSET + 7) % 7;

    // Built with DOM calls rather than an HTML string. Values here come from the API,
    // and a booking widget is not the place to be assembling markup out of data.
    grid.textContent = '';

    // role="grid" needs rows. Cells hung straight off the grid made the month read
    // as one long list. The row is display:contents, so the 7-column layout is
    // unaffected while the accessibility tree gets the structure it expects.
    var row = null;
    var column = 0;
    var place = function (cell) {
      if (!row || column === 7) {
        row = document.createElement('div');
        row.className = 'mode-booking__week';
        row.setAttribute('role', 'row');
        grid.appendChild(row);
        column = 0;
      }
      row.appendChild(cell);
      column += 1;
    };

    for (var i = 0; i < lead; i++) {
      var blank = document.createElement('span');
      blank.className = 'mode-booking__day mode-booking__day--empty';
      blank.setAttribute('role', 'gridcell');
      place(blank);
    }

    for (var day = 1; day <= daysInMonth; day++) {
      var iso = year + '-' + pad(month) + '-' + pad(day);
      var slots = open[iso];
      var isSelected = iso === this.selectedDate;

      if (slots) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className =
          'mode-booking__day' + (isSelected ? ' mode-booking__day--selected' : '');
        button.dataset.date = iso;
        button.textContent = String(day);
        button.setAttribute('role', 'gridcell');
        button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        button.setAttribute(
          'aria-label',
          slots.length + ' times available on ' + longDate(iso)
        );
        if (isSelected) button.setAttribute('aria-current', 'date');
        button.addEventListener('click', function (event) {
          self.selectDate(event.currentTarget.dataset.date);
        });
        button.addEventListener('keydown', function (event) {
          self.moveCalendarFocus(event);
        });
        place(button);
      } else {
        // Closed days stay in the tree. They used to be aria-hidden, which left a
        // screen-reader user with a month full of holes and no way to know the 24th
        // exists at all - the same problem sighted users had at 0.28 opacity.
        var closed = document.createElement('span');
        closed.className = 'mode-booking__day mode-booking__day--closed';
        closed.setAttribute('role', 'gridcell');
        closed.setAttribute('aria-disabled', 'true');
        closed.setAttribute('aria-label', longDate(iso) + ', no times available');
        closed.textContent = String(day);
        place(closed);
      }
    }
  };

  ModeBooking.prototype.selectDate = function (iso) {
    this.selectedDate = iso;
    this.renderCalendar();
    this.renderSlots();
    var step = this.el.timeStep;
    if (step) step.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  //--------------------------------------------------------------------------
  // Step 3: the times
  //--------------------------------------------------------------------------

  ModeBooking.prototype.renderSlots = function () {
    var self = this;
    var container = this.el.slots;
    if (!container || !this.selectedDate) return;

    var slots = this.daysWithSlots()[this.selectedDate] || [];
    this.show(this.el.timeStep, true);

    container.textContent = '';

    if (!slots.length) {
      var empty = document.createElement('p');
      empty.className = 'mode-booking__empty';
      empty.textContent = 'No times left on this day. Please choose another date.';
      container.appendChild(empty);
      return;
    }

    slots.forEach(function (slot) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'mode-booking__slot';
      button.dataset.start = slot.start_utc;
      button.textContent = slot.label;
      button.addEventListener('click', function (event) {
        self.pickSlot(event.currentTarget.dataset.start, event.currentTarget);
      });
      container.appendChild(button);
    });
  };

  //--------------------------------------------------------------------------
  // The hold
  //--------------------------------------------------------------------------

  ModeBooking.prototype.pickSlot = function (startUtc, button) {
    var self = this;
    if (this.busy) return;

    this.setBusy(true);
    this.setStatus(null);
    if (button) button.disabled = true;

    // Give up the previous hold first, so changing your mind returns the old slot to
    // the pool immediately instead of leaving it dead for half an hour.
    this.releaseCurrentHold()
      .then(function () {
        return self.api('/holds', {
          method: 'POST',
          body: {
            variant_gid: self.variantGid(),
            location_id: self.location.id,
            start_utc: startUtc,
            visitor_id: self.visitorId
          }
        });
      })
      .then(function (hold) {
        hold.variant_id = self.variantId;
        self.hold = hold;
        self.saveSessionHold();
        self.injectHoldProperties();
        self.setBusy(false);
        self.renderHeld();
        self.startCountdown();
        self.stopPolling();
      })
      .catch(function (err) {
        self.setBusy(false);
        if (button) button.disabled = false;

        if (err.code === 'SLOT_TAKEN' || err.code === 'SLOT_NOT_OFFERED') {
          // The race we designed for. Someone else's hold committed first; show the
          // real state rather than pretending.
          self.setStatus(self.copy.slotTaken, 'warn');
          return self.loadAvailability({ quiet: true });
        }

        if (err.code === 'ACTIVE_HOLD_LIMIT' || err.code === 'DUPLICATE_HOLD') {
          // Held in another tab, for this same product. The server's message states the
          // fact but not the way out of it, so say what the customer can actually do.
          self.setStatus(self.copy.existingHold, 'warn');
          return undefined;
        }

        if (err.code === 'VISITOR_HOLD_LIMIT') {
          // At the cap of three live holds, on three other products. Deliberately not
          // the message above: there is no tab to go and finish, so telling them to
          // find one sends them looking for something that does not exist.
          self.setStatus(self.copy.holdLimit, 'warn');
          return undefined;
        }

        if (err.code === 'RATE_LIMITED') {
          self.setStatus(err.message, 'warn');
          return undefined;
        }

        self.setStatus(err.message || self.copy.holdFailed, 'error');
        return undefined;
      });
  };

  ModeBooking.prototype.releaseCurrentHold = function () {
    var self = this;
    if (!this.hold || !this.hold.hold_id) return Promise.resolve();

    var hold = this.hold;
    this.hold = null;

    return this.api('/holds/' + hold.hold_id + '/release', {
      method: 'POST',
      body: { token: hold.token }
    }).catch(function () {
      // Best effort. If this fails the hold simply expires on its own, which is why the
      // timeout exists - the release call is an optimisation, never a correctness step.
      return undefined;
    });
  };

  ModeBooking.prototype.discardHold = function (reason) {
    var self = this;
    this.stopCountdown();
    return this.releaseCurrentHold().then(function () {
      self.clearSessionHold();
      self.removeHoldProperties();
      self.show(self.el.held, false);
      self.setSubmitEnabled(!(self.config && self.config.bookable));
      if (reason && window.console) console.debug('[mode-booking] hold discarded:', reason);
    });
  };

  ModeBooking.prototype.renderHeld = function () {
    var hold = this.hold;
    if (!hold) {
      this.show(this.el.held, false);
      this.show(this.el.dateStep, true);
      return;
    }

    this.show(this.el.dateStep, false);
    this.show(this.el.timeStep, false);
    this.show(this.el.held, true);

    this.el.heldWhen.textContent = hold.summary.display;
    this.el.heldWhere.textContent =
      hold.summary.location_name + (hold.summary.address ? ' - ' + hold.summary.address : '');

    this.setStatus(null);
    this.setSubmitEnabled(!hold.submitted);
    if (hold.submitted) {
      this.setStatus('This appointment has already been added. Continue to your cart.', 'info');
    }
  };

  //--------------------------------------------------------------------------
  // Countdown
  //--------------------------------------------------------------------------

  ModeBooking.prototype.startCountdown = function () {
    var self = this;
    this.stopCountdown();
    if (!this.hold) return;

    var tick = function () {
      var remaining = Math.floor(
        (new Date(self.hold.expires_at).getTime() - Date.now()) / 1000
      );

      if (remaining <= 0) {
        self.onHoldExpired();
        return;
      }

      self.el.countdown.textContent =
        'Complete checkout within ' + clock(remaining) + ' to keep this time';
      self.el.countdown.className =
        'mode-booking__countdown' + (remaining < 300 ? ' mode-booking__countdown--urgent' : '');
    };

    tick();
    this.countdownTimer = setInterval(tick, 1000);
  };

  ModeBooking.prototype.stopCountdown = function () {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.countdownTimer = null;
  };

  ModeBooking.prototype.onHoldExpired = function () {
    var self = this;
    this.stopCountdown();
    this.hold = null;
    this.clearSessionHold();
    this.removeHoldProperties();
    self.show(self.el.held, false);
    self.show(self.el.dateStep, true);
    self.setSubmitEnabled(false);
    self.setStatus('Your held time has expired. Please pick a time again.', 'warn');
    self.startPolling();
    self.loadAvailability({ quiet: true }).catch(function () { return undefined; });
  };

  //--------------------------------------------------------------------------
  // Real product-form integration
  //--------------------------------------------------------------------------

  ModeBooking.prototype.holdProperties = function (hold) {
    var props = {};
    // Human-readable, so the customer sees it in the cart and on the order.
    props[WORKSHOP_PROP] = hold.summary.location_name;
    props[APPOINTMENT_PROP] = hold.summary.display;
    // Hidden (underscore-prefixed): what the webhook uses to confirm the booking.
    props[HOLD_ID_PROP] = hold.hold_id;
    props[TOKEN_PROP] = hold.token;
    props[START_PROP] = hold.summary.start_utc;
    props[END_PROP] = hold.summary.end_utc;
    return props;
  };

  ModeBooking.prototype.sessionKey = function () {
    return 'mode_booking_hold:' + String(this.variantId || 'unknown');
  };

  ModeBooking.prototype.saveSessionHold = function () {
    if (!this.hold) return;
    try {
      sessionStorage.setItem(this.sessionKey(), JSON.stringify(this.hold));
    } catch (e) {
      return;
    }
  };

  ModeBooking.prototype.clearSessionHold = function () {
    try {
      sessionStorage.removeItem(this.sessionKey());
    } catch (e) {
      return;
    }
  };

  ModeBooking.prototype.injectHoldProperties = function () {
    if (!this.form || !this.hold) return;
    this.removeHoldProperties();
    var properties = this.holdProperties(this.hold);
    Object.keys(properties).forEach(function (name) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + name + ']';
      input.value = properties[name];
      input.dataset.modeBookingProperty = name;
      this.form.appendChild(input);
    }, this);
    this.forceQuantityOne();
  };

  ModeBooking.prototype.removeHoldProperties = function () {
    if (!this.form) return;
    this.form.querySelectorAll('[data-mode-booking-property]').forEach(function (input) {
      input.remove();
    });
  };

  ModeBooking.prototype.forceQuantityOne = function () {
    if (!this.form) return;
    this.form.querySelectorAll('[name="quantity"]:not([data-mode-booking-quantity])').forEach(function (input) {
      input.value = '1';
      input.disabled = true;
      var container = input.closest(
        'quantity-input, .quantity, [data-quantity], .product-form__quantity'
      );
      if (container && !container.dataset.modeBookingHidden) {
        // A bare `hidden` attribute is only a UA-level display:none, and this theme
        // sets `display: inline-flex` on the wrapper - which wins. That left the
        // stepper's - and + buttons sitting on the page with no number between them.
        // The class carries !important, so the control goes away for real.
        container.dataset.modeBookingHidden = 'true';
        container.setAttribute('hidden', '');
        container.classList.add('mode-booking-quantity-locked');
        container.setAttribute('aria-hidden', 'true');
      }
    });
    var submittedQuantity = this.form.querySelector('[data-mode-booking-quantity]');
    if (!submittedQuantity) {
      submittedQuantity = document.createElement('input');
      submittedQuantity.type = 'hidden';
      submittedQuantity.name = 'quantity';
      submittedQuantity.dataset.modeBookingQuantity = 'true';
      this.form.appendChild(submittedQuantity);
    }
    submittedQuantity.value = '1';
  };

  ModeBooking.prototype.restoreQuantity = function () {
    if (!this.form) return;
    this.form.querySelectorAll('[data-mode-booking-quantity]').forEach(function (input) {
      input.remove();
    });
    this.form.querySelectorAll('[name="quantity"]').forEach(function (input) {
      input.disabled = false;
    });
    this.form.querySelectorAll('[data-mode-booking-hidden="true"]').forEach(function (element) {
      element.removeAttribute('hidden');
      element.removeAttribute('aria-hidden');
      element.classList.remove('mode-booking-quantity-locked');
      delete element.dataset.modeBookingHidden;
    });
  };

  ModeBooking.prototype.hasMatchingInjectedHold = function () {
    if (!this.form || !this.hold || String(this.hold.variant_id) !== String(this.variantId)) {
      return false;
    }
    var holdInput = this.form.querySelector('[data-mode-booking-property="' + HOLD_ID_PROP + '"]');
    var tokenInput = this.form.querySelector('[data-mode-booking-property="' + TOKEN_PROP + '"]');
    return !!(
      holdInput && tokenInput &&
      holdInput.value === this.hold.hold_id && tokenInput.value === this.hold.token
    );
  };

  ModeBooking.prototype.restoreFromSession = function () {
    var self = this;
    var saved;
    try {
      saved = JSON.parse(sessionStorage.getItem(this.sessionKey()) || 'null');
    } catch (e) {
      saved = null;
    }
    if (!saved || !saved.hold_id || !saved.token) {
      this.show(this.el.dateStep, !!this.location);
      this.startPolling();
      return Promise.resolve();
    }

    return this.api('/holds/' + saved.hold_id + '?token=' + encodeURIComponent(saved.token))
        .then(function (status) {
          if (status.status !== 'held' || status.seconds_remaining <= 0) {
            return self.onInvalidRestoredHold();
          }

          self.hold = {
            hold_id: saved.hold_id,
            token: saved.token,
            expires_at: status.expires_at,
            variant_id: self.variantId,
            submitted: saved.submitted === true,
            summary: {
              display: status.summary.display,
              location_id: status.summary.location_id,
              location_name: status.summary.location_name,
              start_utc: status.summary.start_utc,
              end_utc: status.summary.end_utc,
              address: status.summary.address
            }
          };

          // Re-select the workshop the hold actually belongs to, by id.
          var match = self.locations.filter(function (l) {
            return l.id === status.summary.location_id;
          })[0];
          if (match) {
            self.location = match;
            if (self.el.locationSelect) self.el.locationSelect.value = match.id;
          }

          self.injectHoldProperties();
          self.renderHeld();
          self.startCountdown();
          return undefined;
        })
        .catch(function (error) {
          if (error.status === 404 || error.status === 410) return self.onInvalidRestoredHold();
          self.setSubmitEnabled(false);
          self.setStatus('We could not verify your held time. Check your connection and retry.', 'error');
          return undefined;
        });
  };

  ModeBooking.prototype.onInvalidRestoredHold = function () {
    this.hold = null;
    this.clearSessionHold();
    this.removeHoldProperties();
    this.show(this.el.held, false);
    this.setSubmitEnabled(false);
    this.setStatus('Your previously held time has expired. Please pick a time again.', 'warn');
    this.startPolling();
    return this.loadAvailability({ quiet: true });
  };

  /**
   * Stop the product form and accelerated checkout while a mapped variant has no hold.
   *
   * This protects the normal storefront journey on non-Plus stores. Browser controls
   * can still be bypassed with a direct Cart API request, so orders/create remains the
   * final safety net and raises booking.needs_booking for staff intervention.
   */
  ModeBooking.prototype.setSubmitEnabled = function (enabled) {
    if (!this.form) return;
    var buttons = this.form.querySelectorAll(
      '[type="submit"], [name="add"], .shopify-payment-button button'
    );
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].disabled = !enabled;
      buttons[i].classList.toggle('mode-booking-blocked', !enabled);
    }
    this.form.classList.toggle('mode-booking-checkout-blocked', !enabled);
    this.renderSubmitHint(!enabled && !!(this.config && this.config.bookable));
  };

  /** The primary buy button, used as the scroll target and the hint's anchor. */
  ModeBooking.prototype.submitButton = function () {
    if (!this.form) return null;
    return this.form.querySelector('[name="add"], [type="submit"]');
  };

  /**
   * Say why the buy button is dead, immediately under the buy button.
   *
   * A disabled control with no explanation is the single most common way a booking
   * flow loses someone: they scroll past the calendar, find a greyed button, tap it,
   * nothing happens, and there is no text anywhere connecting the two.
   */
  ModeBooking.prototype.renderSubmitHint = function (show) {
    var button = this.submitButton();
    if (!button) return;
    var hint = this.hintEl;
    if (!hint) {
      hint = this.form.querySelector('[data-mode-booking-hint]');
    }
    if (!hint) {
      if (!show) return;
      hint = document.createElement('p');
      hint.className = 'mode-booking-hint';
      hint.setAttribute('data-mode-booking-hint', '');
      hint.id = 'mode-booking-hint-' + (this.sectionId || 'default');
      button.insertAdjacentElement('afterend', hint);
    }
    this.hintEl = hint;
    hint.textContent = this.copy.hint;
    hint.hidden = !show;
    // Tie the reason to the control for anyone not reading the layout.
    if (show) button.setAttribute('aria-describedby', hint.id);
    else button.removeAttribute('aria-describedby');
  };

  ModeBooking.prototype.moveCalendarFocus = function (event) {
    var delta = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[event.key];
    if (!delta) return;
    var buttons = Array.prototype.slice.call(
      this.el.calendarDays.querySelectorAll('button.mode-booking__day')
    );
    var index = buttons.indexOf(event.currentTarget);
    var next = buttons[index + delta];
    if (!next) return;
    event.preventDefault();
    next.focus();
  };

  //--------------------------------------------------------------------------
  // Polling
  //--------------------------------------------------------------------------

  ModeBooking.prototype.startPolling = function () {
    var self = this;
    this.stopPolling();
    this.pollTimer = setInterval(function () {
      // Only while the picker is actually open and on screen. A backgrounded tab does
      // not need live inventory, and hammering the proxy from every idle tab would be
      // rude.
      if (document.visibilityState !== 'visible') return;
      if (self.hold) return;
      if (!self.location) return;
      if (self.el.dateStep && self.el.dateStep.hidden) return;
      self.loadAvailability({ quiet: true }).catch(function () {
        return undefined;
      });
    }, this.refreshMs);
  };

  /** Push the next tick a full interval out. No-op when polling is not running. */
  ModeBooking.prototype.restartPolling = function () {
    if (this.pollTimer) this.startPolling();
  };

  ModeBooking.prototype.stopPolling = function () {
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = null;
  };

  //--------------------------------------------------------------------------
  // Events
  //--------------------------------------------------------------------------

  ModeBooking.prototype.bind = function () {
    var self = this;

    if (this.form) {
      this.form.addEventListener(
        'submit',
        function (event) {
          if (!self.config || !self.config.bookable) return;
          if (self.submitting) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }
          if (self.hasMatchingInjectedHold()) {
            self.forceQuantityOne();
            self.submitting = true;
            self.hold.submitted = true;
            self.saveSessionHold();
            setTimeout(function () {
              self.setSubmitEnabled(false);
            }, 0);
            return;
          }
          event.preventDefault();
          event.stopImmediatePropagation();
          self.setStatus('Please select an appointment before adding this item.', 'warn');
          self.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
        true
      );
    }

    ['cart:error', 'product-form:error'].forEach(function (name) {
      document.addEventListener(name, function () {
        if (!self.hold || !self.submitting) return;
        self.submitting = false;
        self.hold.submitted = false;
        self.saveSessionHold();
        self.setSubmitEnabled(true);
      });
    });

    if (this.el.locationSelect) {
      this.el.locationSelect.addEventListener('change', function (event) {
        self.discardHold('workshop changed').then(function () {
          self.selectLocation(event.target.value);
          self.startPolling();
        });
      });
    }

    if (this.el.calendarPrev) {
      this.el.calendarPrev.addEventListener('click', function () {
        self.stepMonth(-1);
      });
    }
    if (this.el.calendarNext) {
      this.el.calendarNext.addEventListener('click', function () {
        self.stepMonth(1);
      });
    }

    if (this.el.changeTime) {
      this.el.changeTime.addEventListener('click', function () {
        // If the line is already in the cart it keeps the OLD appointment until a new
        // time is picked and the properties are rewritten. Say so, rather than
        // leaving a cart the customer believes they have just changed.
        //
        // `submitted` is the signal, not a cart line key: it is set when the product
        // form is submitted, cleared on cart:error, and survives the page load through
        // sessionStorage - so it is still true after a full-page add to cart, which is
        // exactly the case this warning exists for.
        var stale = !!(self.hold && self.hold.submitted);
        self.discardHold('customer chose to change').then(function () {
          self.setStatus(stale ? self.copy.cartStale : null, stale ? 'warn' : undefined);
          self.show(self.el.dateStep, true);
          self.startPolling();
          return self.loadAvailability({ quiet: true });
        });
      });
    }

    // Variant switching. Themes announce this several different ways, so listen for
    // common events and observe the scoped form's variant input.
    ['variant:change', 'variantChange', 'variant_change'].forEach(function (name) {
      document.addEventListener(name, function () {
        self.syncVariant();
      });
    });

    if (this.form) {
      this.form.addEventListener('change', function () {
        queueMicrotask(function () { self.syncVariant(); });
      });
      this.variantObserver = new MutationObserver(function () {
        queueMicrotask(function () { self.syncVariant(); });
      });
      this.variantObserver.observe(this.form, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['value', 'checked']
      });
    }

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible' && !self.hold && self.location) {
        self.loadAvailability({ quiet: true }).catch(function () {
          return undefined;
        });
      }
    });
  };

  ModeBooking.prototype.syncVariant = function () {
    if (this.destroyed) return;
    var current = this.currentFormVariantId();
    if (!current || current === this.variantId) return;

    var self = this;
    this.variantId = current;

    // A hold belongs to the variant it was created for. Switching to "Mail-in", or to a
    // different unlock, must not carry the old appointment across.
    this.discardHold('variant changed').then(function () {
      self.selectedDate = null;
      self.availability = null;
      self.show(self.el.timeStep, false);
      return self.loadForVariant();
    });
  };

  ModeBooking.prototype.destroy = function () {
    this.destroyed = true;
    this.stopPolling();
    this.stopCountdown();
    this.inflight = null;
    if (this.abortController) this.abortController.abort();
    if (this.variantObserver) this.variantObserver.disconnect();
  };

  ModeBooking.prototype.stepMonth = function (direction) {
    var months = uniq(
      Object.keys(this.daysWithSlots())
        .sort()
        .map(function (d) {
          return d.slice(0, 7);
        })
    );
    var index = months.indexOf(this.visibleMonth);
    var next = months[index + direction];
    if (!next) return;
    this.visibleMonth = next;
    this.renderCalendar();
  };

  //--------------------------------------------------------------------------
  // Small helpers
  //--------------------------------------------------------------------------

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function clock(totalSeconds) {
    var m = Math.floor(totalSeconds / 60);
    var s = totalSeconds % 60;
    return m + ':' + pad(s);
  }

  function addDays(iso, days) {
    var d = new Date(iso + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  }

  function monthLabel(year, month) {
    return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(undefined, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC'
    });
  }

  function longDate(iso) {
    return new Date(iso + 'T00:00:00Z').toLocaleDateString(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: 'UTC'
    });
  }

  function uniq(list) {
    return list.filter(function (value, index) {
      return list.indexOf(value) === index;
    });
  }

  function visitorId() {
    var key = 'mode_booking_visitor_v1';
    try {
      var existing = localStorage.getItem(key);
      if (existing) return existing;
      var created = window.crypto && crypto.randomUUID
        ? crypto.randomUUID()
        : Array.from(crypto.getRandomValues(new Uint8Array(24)), function (byte) {
            return byte.toString(16).padStart(2, '0');
          }).join('');
      localStorage.setItem(key, created);
      return created;
    } catch (e) {
      return 'session-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    }
  }

  //--------------------------------------------------------------------------
  // Start
  //--------------------------------------------------------------------------

  function boot() {
    document.querySelectorAll('[data-mode-booking]').forEach(function (root) {
      if (root.dataset.modeBookingReady === 'true') return;
      root.dataset.modeBookingReady = 'true';
      root.modeBookingInstance = new ModeBooking(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-boot when the section is re-rendered.
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', function (event) {
    var roots = event.target.querySelectorAll('[data-mode-booking]');
    roots.forEach(function (root) {
      if (root.modeBookingInstance) root.modeBookingInstance.destroy();
    });
  });
})();
